/**
 * Dipendente dello studio: fine della sessione e studio non in regola (Ruling R15/R16).
 *
 * - 401 `DIPENDENTI_SPENTI` / `SESSIONE_NON_VALIDA` → la sessione è finita: si avvisano gli
 *   ascoltatori (FineSessioneDipendente, montato in App: logout completo, toast, pagina di accesso)
 *   e poi si tolgono comunque token e dati, così le risposte successive non riavvisano.
 * - 403 `STUDIO_NON_IN_REGOLA` → la sessione resta; lo studio risulta chiuso finché una lettura di
 *   `/auth/me` non torna valida (sezioni chiuse, avviso sulla dashboard).
 *
 * Il titolare non è mai toccato: tutto passa solo se la sessione salvata è di un dipendente
 * (e il backend non manda questi codici al token del titolare).
 *
 * Modulo senza axios né otpAuthService: lo usano l'intercettore di otpAuthService, il client
 * Apollo dei sondaggi, le guardie e la dashboard.
 */
import { codiceDaRisposta, motivoFineDipendente, MotivoFineDipendente } from '../utils/sessioneAssociato';
import { sessioneDiDipendente } from '../utils/sezioniDipendente';

const ascoltatori = new Set<(motivo: MotivoFineDipendente) => void>();
let studioNonInRegola = false;

/** Registra un ascoltatore; restituisce la funzione che lo toglie. */
export const alFineSessioneDipendente = (fn: (motivo: MotivoFineDipendente) => void): (() => void) => {
  ascoltatori.add(fn);
  return () => {
    ascoltatori.delete(fn);
  };
};

const avvisa = (motivo: MotivoFineDipendente): void => {
  Array.from(ascoltatori).forEach((fn) => {
    try {
      fn(motivo);
    } catch (e) {
      // un ascoltatore guasto non ferma gli altri
    }
  });
};

/** Lo studio del dipendente è risultato non in regola (403) e non è ancora tornato valido. */
export const studioDelDipendenteNonInRegola = (): boolean => studioNonInRegola;

/** `/auth/me` valida: lo studio torna aperto. */
export const segnaStudioInRegola = (): void => {
  studioNonInRegola = false;
};

/** Al logout (e nei test): nessuno stato residuo. */
export const azzeraStatoDipendente = (): void => {
  studioNonInRegola = false;
};

/**
 * Intestazione Authorization di una richiesta axios (`config.headers`, oggetto semplice o AxiosHeaders).
 * undefined se la configurazione non c'è (chiamante che non la conosce), null se manca l'intestazione.
 */
export const autorizzazioneRichiesta = (config: unknown): string | null | undefined => {
  if (!config || typeof config !== 'object') return undefined;
  const headers = (config as { headers?: unknown }).headers as
    | ({ get?: (nome: string) => unknown } & Record<string, unknown>)
    | undefined
    | null;
  if (!headers || typeof headers !== 'object') return null;
  let valore: unknown;
  try {
    valore = typeof headers.get === 'function' ? headers.get('Authorization') : undefined;
  } catch (e) {
    valore = undefined;
  }
  if (valore == null) valore = headers.Authorization ?? headers.authorization;
  return typeof valore === 'string' && valore ? valore : null;
};

/** Il 401 viene da una richiesta fatta con il token salvato adesso? (no: richiesta vecchia, si ignora) */
const conTokenAttuale = (autorizzazione: string | null): boolean => {
  let token: string | null = null;
  try {
    token = window.localStorage.getItem('associate_token');
  } catch (e) {
    token = null;
  }
  return !!token && autorizzazione === `Bearer ${token}`;
};

/**
 * Esamina una risposta (status + corpo REST o GraphQL). Restituisce il motivo se l'ha gestita,
 * altrimenti null (titolare, altri codici, sessione già chiusa). Non lancia mai.
 * `autorizzazione`: l'intestazione Authorization della richiesta fallita. Se il chiamante la passa,
 * un 401 di fine sessione vale solo se la richiesta usava il token salvato adesso (una richiesta
 * partita col token di prima, es. subito dopo il cambio password, non chiude la sessione nuova).
 */
export const gestisciRispostaDipendente = (
  status: number | undefined | null,
  corpo: unknown,
  autorizzazione?: string | null
): MotivoFineDipendente | null => {
  try {
    const motivo = motivoFineDipendente(status, codiceDaRisposta(corpo));
    if (!motivo || !sessioneDiDipendente()) return null;
    if (motivo === 'sessione' && autorizzazione !== undefined && !conTokenAttuale(autorizzazione)) return null;
    if (motivo === 'sessione') {
      // Prima gli ascoltatori (il logout completo legge ancora il token), poi la pulizia di sicurezza
      avvisa('sessione');
      try {
        window.localStorage.removeItem('associate_token');
        window.localStorage.removeItem('associate_data');
      } catch (e) {
        // storage non disponibile
      }
      studioNonInRegola = false;
      return motivo;
    }
    if (!studioNonInRegola) {
      studioNonInRegola = true;
      avvisa('studio_non_in_regola');
    }
    return motivo;
  } catch (e) {
    return null;
  }
};
